import { useRef } from 'react';
import { Lock, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { filterMenusWithPlan } from '../../routes';
import { useOverlayScrollLock } from '../../components/useOverlayScrollLock';
import EstablishmentSwitcher from './EstablishmentSwitcher';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAppContext } from '../../contexts/AppContext';
import { usePlanAccess } from '../../plans/usePlanAccess';
import { Logo } from '../Logo';
import { ThemeToggle } from '../../components/ThemeToggle';
import { getMenuSectionKey, MENU_SECTIONS } from './menuSections';
import { MenuAccountCard } from './MenuAccountCard';

export default function MenuDrawer() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuthContext();
    const { showDrawerMenu, toggleShowDrawerMenu } = useAppContext();
    const { menuShowEntry, hasFeature, restrictionsApply } = usePlanAccess();
    const scrollRef = useRef<HTMLDivElement>(null);

    useOverlayScrollLock(scrollRef, !!showDrawerMenu);

    const visibleMenus = filterMenusWithPlan(user, menuShowEntry);
    const isClientOnly = !!user?.client && !user?.admin && !user?.worker && !user?.root;
    const groupedMenus = MENU_SECTIONS.map((section) => ({
        ...section,
        items: visibleMenus.filter((menu) => getMenuSectionKey(menu.path) === section.key),
    })).filter((section) => section.items.length > 0);
    const otherMenus = visibleMenus.filter((menu) => getMenuSectionKey(menu.path) === 'outros');

    const itemClassName = (isActive = false) =>
        [
            'sf-menu-item flex min-h-10 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium',
            isActive ? 'is-active' : '',
        ].join(' ');

    const renderMenuItem = ({ title, icon: Icon, path, planFeature }: (typeof visibleMenus)[number]) => {
        const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
        const lockedByPlan = !!(
            restrictionsApply &&
            planFeature &&
            (user?.admin || user?.root) &&
            !hasFeature(planFeature)
        );

        return (
            <li key={path}>
                <button
                    type="button"
                    className={itemClassName(isActive) + (lockedByPlan ? ' opacity-[0.72]' : '')}
                    onClick={() => {
                        navigate(path);
                        toggleShowDrawerMenu();
                    }}
                    aria-current={isActive ? 'page' : undefined}
                >
                    <Icon className="sf-menu-item-icon" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{title}</span>
                    {lockedByPlan && <Lock size={12} strokeWidth={2} className="shrink-0 opacity-60" aria-hidden="true" />}
                </button>
            </li>
        );
    };

    return (
        <div className="sf-menu-shell sf-mobile-menu-shell flex h-full w-[86vw] max-w-[320px] flex-col rounded-r-[1.35rem] px-2 pb-2 pt-3">
            <div className="relative px-3 pb-4 pt-1">
                <div className="flex items-center justify-center">
                    <Logo />
                    <button
                        type="button"
                        className="sf-icon-button sf-close-button btn btn-circle btn-xs absolute right-5 top-5 shrink-0"
                        onClick={toggleShowDrawerMenu}
                        aria-label="Fechar menu"
                    >
                        <X size={14} strokeWidth={2} aria-hidden="true" />
                    </button>
                </div>
            </div>

            <div ref={scrollRef} className="menu-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1 py-3">
                <ul className="menu gap-0.5 p-0">
                    {(isClientOnly
                        ? [{ key: 'cliente', title: '', items: visibleMenus }]
                        : [...groupedMenus, ...(otherMenus.length ? [{ key: 'outros', title: 'Outros', items: otherMenus }] : [])]
                    ).map((section, sectionIndex) => (
                        <li key={section.key} className={`sf-menu-group ${sectionIndex > 0 ? 'mt-4' : ''}`}>
                            {section.title && (
                                <div className="sf-menu-section-label px-3 pb-1 pt-1 text-[11px] font-semibold text-base-content/45">
                                    {section.title}
                                </div>
                            )}
                            <ul className="menu gap-0.5 p-0">{section.items.map(renderMenuItem)}</ul>
                        </li>
                    ))}
                    <li className="mt-3">
                        <ThemeToggle withLabel className={itemClassName(false)} labelClassName="truncate" />
                    </li>
                </ul>
            </div>

            <div className="sf-menu-footer mt-auto space-y-3 px-1 pb-[calc(0.25rem+env(safe-area-inset-bottom))] pt-2">
                <ul className="menu gap-0.5 p-0">
                    <EstablishmentSwitcher />
                </ul>
                <MenuAccountCard onNavigate={toggleShowDrawerMenu} />
            </div>
        </div>
    );
}
